import {S, WhiteCard, RouteSkeleton, SummarySkeleton} from './SkeletonCards.jsx'

/**
 * Loading state for `/rides/:rideId/phone-booking-confirmation`.
 *
 * Its own file rather than SkeletonCards, because that one traces RideConfirm's stack and
 * this page's is a different shape: the ride card and the comparison sit ABOVE the form,
 * and the commit sits OUTSIDE the grey panel rather than as the last row inside it. A
 * skeleton that shows the wrong shape is worse than none — the layout visibly rearranges
 * itself the moment the data lands.
 *
 * Everything is built from SkeletonCards' own bars, so the two can't drift in colour,
 * radius or pulse.
 *
 * ⚠️ Mirrors PhoneBookingConfirmation's render order. Move a section there, move it here.
 */
export default function PhoneBookingSkeleton() {
    return (
        <>
            {/* Title */}
            <div className="max-w-3xl mx-auto text-center mb-5">
                <S w="35%" h={30} className="mx-auto"/>
            </div>

            {/* 1. Ride card — car on the left, the dark price panel on the right. The
                   panel's three perk rows are what make it the taller half. */}
            <div className="max-w-3xl mx-auto mb-7">
                <div className="bg-white p-4 rounded-[18px] shadow-[0_4px_24px_#201e2014] flex gap-3.5 items-stretch max-md:flex-col">
                    <div className="flex-1 min-w-0">
                        <S w="45%" h={24} mb={10}/>
                        <S w="80%" h={14} mb={20}/>
                        <S w="100%" h={150} r={12}/>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-3">
                        <S w="100%" h={110} r={14}/>
                        <S w="100%" h={62} r={12}/>
                        <S w="100%" h={62} r={12}/>
                        <S w="100%" h={62} r={12}/>
                    </div>
                </div>
            </div>

            {/* 2. Reviews bar, comparison card, perks bar, popular questions */}
            <div className="max-w-3xl mx-auto mb-5 flex flex-col gap-7">
                <S w="100%" h={72} r={16}/>
                <S w="100%" h={230} r={16}/>
                <S w="100%" h={56} r={20}/>
                <S w="100%" h={120} r={16}/>
            </div>

            {/* 3. The confirm stack, in its grey panel */}
            <div className="max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:bg-transparent">
                <RouteSkeleton/>
                <RouteSkeleton/>

                {/* Date & Time */}
                <WhiteCard>
                    <S w="40%" h={18} mb={14}/>
                    <div className="flex gap-3">
                        <S w="50%" h={44} r={10}/>
                        <S w="50%" h={44} r={10}/>
                    </div>
                </WhiteCard>

                {/* Passengers / Child Seats / Luggage / Oversized */}
                <SummarySkeleton/>
                <SummarySkeleton/>
                <SummarySkeleton/>
                <SummarySkeleton/>

                {/* Special Request + Vehicle Detail */}
                <div className="flex gap-4 items-stretch max-md:flex-col">
                    <WhiteCard>
                        <S w="70%" h={14} mb={10}/>
                        <S w="60%" h={14} mb={10}/>
                        <S w="50%" h={14} mb={16}/>
                        <S w="100%" h={90} r={16}/>
                    </WhiteCard>
                    <WhiteCard>
                        <div className="flex items-center gap-3 mb-5">
                            <S w={48} h={48} r={8}/>
                            <S w="50%" h={18}/>
                        </div>
                        <S w="100%" h={14} mb={10}/>
                        <S w="100%" h={14} mb={10}/>
                        <S w="100%" h={14} mb={16}/>
                        <S w={40} h={40} r={9999} className="ml-auto"/>
                    </WhiteCard>
                </div>

                {/* Payment Methods */}
                <WhiteCard>
                    <S w="40%" h={18} mb={16}/>
                    <S w="100%" h={56} r={16} mb={12}/>
                    <S w="100%" h={56} r={16}/>
                </WhiteCard>
            </div>

            {/* 4. Reserve Ride + trust strip + terms — OUTSIDE the panel, as on the page */}
            <div className="max-w-3xl mx-auto mt-5 flex flex-col gap-6 max-md:px-4">
                <div className="flex flex-col gap-5 items-stretch">
                    <S w="100%" h={72} r={9999}/>
                    <S w={340} h={78} r={16} className="mx-auto max-w-full"/>
                </div>
                <S w="70%" h={12} className="mx-auto"/>
            </div>
        </>
    )
}
